import { z } from 'zod'

/**
 * The Customer.io transactional message identifier the attendance emails send
 * with — currently just the parent-absence office notice — read and written as
 * platform configuration.
 *
 * Sibling of `event-email-message-ids`, deliberately NOT the same key: the two
 * products' launch state must not be coupled into one row. See
 * `services/platform-entities/sections/attendance/attendance-email-config.ts`.
 *
 * Clearing a field means REMOVING it from the stored object, never writing an
 * empty string: an empty string wins the platform/school merge and would take
 * that school off email entirely rather than returning it to the platform
 * default.
 */

/**
 * `null` clears the identifier (removing the key), returning the school to the
 * platform default.
 *
 * Unlike the events request this is not a partial write, because there is only
 * one field — an "at least one field present" refinement over a single key is
 * just a required key spelled indirectly. When a second identifier joins this
 * object, make both `.optional()` and add the refinement then.
 */
export const PutAttendanceEmailMessageIdsRequestSchema = z.object({
  parentAbsenceNoticeMessageId: z.string().trim().min(1).nullable()
})

export type PutAttendanceEmailMessageIdsRequest = z.infer<typeof PutAttendanceEmailMessageIdsRequestSchema>

/**
 * Provenance, not just the resolved value.
 *
 * An admin who sees only a resolved value and edits it is one save away from
 * overwriting the platform default while believing they are editing their own
 * school's. The three values are read from the two rows independently rather
 * than inferred by comparison — a school override that happens to equal the
 * platform value is still an override, and a comparison-based implementation
 * reports it as inherited.
 */
export const AttendanceEmailMessageIdProvenanceSchema = z.object({
  platform: z.string().nullable(),
  school: z.string().nullable(),
  resolved: z.string().nullable(),
  /** True when the school row supplies this field, even if it equals the platform value. */
  overridden: z.boolean()
})

export const AttendanceEmailMessageIdsResponseSchema = z.object({
  parentAbsenceNotice: AttendanceEmailMessageIdProvenanceSchema
})

export type AttendanceEmailMessageIdProvenance = z.infer<typeof AttendanceEmailMessageIdProvenanceSchema>
export type AttendanceEmailMessageIdsResponse = z.infer<typeof AttendanceEmailMessageIdsResponseSchema>
